import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { DashboardDailyPoint } from './dashboard.types';

const WIDTH = 600;
const HEIGHT = 200;
const Y_AXIS_WIDTH = 64;
const MAX_X_TICKS = 6;

/** Rounds a chart max up to a clean-looking number, e.g. 8,430 -> 9,000. */
function niceCeiling(value: number) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = magnitude / 2;
  return Math.ceil(value / step) * step;
}

/** The plotted figure. Net sales, as the old Trends chart did. */
const current = (p: DashboardDailyPoint) => p.sales.netSales.value;
const previous = (p: DashboardDailyPoint) => p.sales.netSales.previousValue;

function linePath(
  points: DashboardDailyPoint[],
  pick: (p: DashboardDailyPoint) => number,
  max: number
) {
  const stepX = points.length > 1 ? WIDTH / (points.length - 1) : 0;
  return points
    .map((p, i) => {
      const y = max === 0 ? HEIGHT : HEIGHT - (pick(p) / max) * HEIGHT;
      return `${i === 0 ? 'M' : 'L'} ${(i * stepX).toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

/**
 * A hand-rolled SVG line rather than a charting dependency — two series and a
 * hover tooltip is all this needs. `preserveAspectRatio="none"` lets the
 * viewBox stretch to the container's actual pixel size for the plot itself;
 * the axis labels sit outside the SVG as plain DOM text so they are never
 * skewed by that non-uniform scaling.
 */
export function RevenueLineChart({
  points,
  previousRangeLabel,
  busy,
}: {
  points: DashboardDailyPoint[];
  previousRangeLabel: string;
  /** The plotted range is being refreshed: the line stays, dimmed. */
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const max = niceCeiling(
    Math.max(1, ...points.flatMap((p) => [current(p), previous(p)]))
  );
  const yTicks = [max, (max * 2) / 3, max / 3, 0];

  const tickCount = Math.min(MAX_X_TICKS, points.length);
  const xTickIndices =
    points.length <= 1
      ? [0]
      : Array.from({ length: tickCount }, (_, i) =>
          Math.round((i * (points.length - 1)) / (tickCount - 1))
        );

  return (
    <div className="bg-card relative rounded-xl border p-3">
      <BusyOverlay show={busy} />
      <div className={cn('space-y-2', busy && 'opacity-60 transition-opacity')}>
        <div className="flex gap-2">
          <div
            className="text-muted-foreground flex shrink-0 flex-col justify-between py-1 text-right text-xs"
            style={{ width: Y_AXIS_WIDTH }}
          >
            {yTicks.map((tick) => (
              <span key={tick}>{formatCurrency(tick)}</span>
            ))}
          </div>

          <div className="relative h-56 min-w-0 flex-1">
            <svg
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              preserveAspectRatio="none"
              className="h-full w-full overflow-visible"
            >
              {yTicks.map((tick) => {
                const y = max === 0 ? HEIGHT : HEIGHT - (tick / max) * HEIGHT;
                return (
                  <line
                    key={tick}
                    x1={0}
                    x2={WIDTH}
                    y1={y}
                    y2={y}
                    stroke="currentColor"
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                    className="text-border"
                  />
                );
              })}
              <path
                d={linePath(points, previous, max)}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeDasharray="5 4"
                vectorEffect="non-scaling-stroke"
                className="text-muted-foreground/60"
              />
              <path
                d={linePath(points, current, max)}
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                vectorEffect="non-scaling-stroke"
                className="text-primary"
              />
            </svg>
            <div className="absolute inset-0 flex">
              {points.map((p) => (
                <Tooltip key={p.date}>
                  <TooltipTrigger asChild>
                    <div className="flex-1" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-semibold">{p.label}</p>
                    <p>
                      {t('dashboard.netSales')}: {formatCurrency(current(p))}
                    </p>
                    <p className="text-muted-foreground">
                      {p.previousLabel}: {formatCurrency(previous(p))}
                    </p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
      </div>

      <div className="flex gap-2">
        <div className="shrink-0" style={{ width: Y_AXIS_WIDTH }} />
        <div className="text-muted-foreground relative h-4 min-w-0 flex-1 text-xs">
          {xTickIndices.map((index) => (
            <span
              key={index}
              className="absolute -translate-x-1/2 whitespace-nowrap first:translate-x-0 last:-translate-x-full"
              style={{
                left: `${points.length > 1 ? (index / (points.length - 1)) * 100 : 0}%`,
              }}
            >
              {points[index].label}
            </span>
          ))}
        </div>
      </div>

      <div className="text-muted-foreground flex flex-wrap gap-4 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="bg-primary inline-block h-0.5 w-4 rounded-full" />
          {t('dashboard.thisPeriod')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="border-muted-foreground/60 inline-block w-4 border-t-2 border-dashed" />
          {previousRangeLabel}
        </span>
      </div>
      </div>
    </div>
  );
}
